import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, where, getDocs, addDoc, updateDoc, doc, getDoc, onSnapshot, setDoc } from '../firebaseConfig';
import Card from '@mui/material/Card';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Divider from '@mui/material/Divider';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useTheme } from '@mui/material/styles';
import './MonthlyPerformanceSummary.css';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import { Radar } from 'react-chartjs-2';
import Modal from '@mui/material/Modal'; // NEW: Import Modal
import MDButton from 'components/MDButton'; // NEW: Replace Button with MDButton
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
// NEW: Import MD5 for caching
import md5 from 'md5';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, ChartTooltip, Legend);

const MonthlyPerformanceSummary = ({ userID, trades, tradeAccountID, onTogglePerformanceView, tradeAccounts }) => {
  const [report, setReport] = useState({});
  const [lastMonthScore, setLastMonthScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const [userCapital, setUserCapital] = useState(null);
  const [showOverallPerformance, setShowOverallPerformance] = useState(true);
  const [chartType, setChartType] = useState('radar');
  const [feedbackFontSize, setFeedbackFontSize] = useState('1rem');
  const [tableFontSize, setTableFontSize] = useState('0.875rem');
  const [tableRowHeight, setTableRowHeight] = useState('auto');
  const [tableHeight, setTableHeight] = useState('auto');
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false); // NEW: State for modal
  const feedbackRef = useRef(null);
  const tableRef = useRef(null);
  const nonTableContentRef = useRef(null);

  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const iconColor = darkMode ? theme.palette.grey[200] : theme.palette.grey[600];

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

    // NEW: Modal open/close handlers
    const handleOpenFeedbackModal = () => setOpenFeedbackModal(true);
    const handleCloseFeedbackModal = () => setOpenFeedbackModal(false);

  // NEW: Helper to load cached data from localStorage
  const loadCachedData = () => {
    const cacheKey = `report_${userID}_${showOverallPerformance ? 'overall' : tradeAccountID}`;
    const cached = localStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : null;
  };

  // NEW: Helper to save data to localStorage
  const saveCachedData = (data) => {
    const cacheKey = `report_${userID}_${showOverallPerformance ? 'overall' : tradeAccountID}`;
    localStorage.setItem(cacheKey, JSON.stringify(data));
  };

  useEffect(() => {
    const loadChartPreference = async () => {
      try {
        const prefQuery = query(collection(db, 'Users', userID, 'preferences'));
        const prefSnapshot = await getDocs(prefQuery);
        const prefs = prefSnapshot.docs[0]?.data();
        if (prefs?.chartType) {
          setChartType(prefs.chartType);
        }
      } catch (error) {
        console.error('Error loading chart preference:', error);
      }
      setLoading(false);
    };
    if (userID) {
      loadChartPreference();
    }
  }, [userID]);

  const saveChartPreference = async (type) => {
    try {
      const prefsRef = collection(db, 'Users', userID, 'preferences');
      const prefQuery = query(prefsRef);
      const prefSnapshot = await getDocs(prefQuery);

      if (prefSnapshot.empty) {
        await addDoc(prefsRef, { chartType: type });
      } else {
        const docRef = doc(db, 'Users', userID, 'preferences', prefSnapshot.docs[0].id);
        await updateDoc(docRef, { chartType: type });
      }
    } catch (error) {
      console.error('Error saving chart preference:', error);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'Users', userID));
        const userData = userDoc.data();
        if (!userData?.createdAt) {
          setUserCreatedAt(new Date(2024, 0, 1));
          return;
        }
        const createdAtDate = userData.createdAt.toDate
          ? userData.createdAt.toDate()
          : new Date(userData.createdAt);
        setUserCreatedAt(createdAtDate);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [userID]);

  useEffect(() => {
    const fetchCapitalData = () => {
      const capitalPath = showOverallPerformance
        ? collection(db, 'Users', userID, 'capital')
        : collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'capital');
      const capitalQuery = query(capitalPath);
      const unsubscribe = onSnapshot(capitalQuery, (snapshot) => {
        let totalDeposits = 0;
        let totalWithdrawals = 0;
        snapshot.forEach((doc) => {
          const data = doc.data();
          totalDeposits += data.deposit || 0;
          totalWithdrawals += data.withdrawal || 0;
        });
        const totalReturnsPromise = showOverallPerformance
          ? getDocs(query(collection(db, 'Users', userID, 'trades'), where('status', '==', 'Closed')))
              .then(snapshot => snapshot.docs.reduce((sum, doc) => sum + (doc.data().returns || 0), 0))
          : Promise.resolve(trades.filter(trade => trade.status === 'Closed').reduce((sum, trade) => sum + (trade.returns || 0), 0));
        
        totalReturnsPromise.then(returns => {
          const initialCapital = 0;
          const currentCapital = initialCapital + totalDeposits - totalWithdrawals + returns;
          setUserCapital(currentCapital > 0 ? currentCapital : 1000);
        });
      });
      return () => unsubscribe();
    };
    if (userID && (showOverallPerformance || tradeAccountID)) {
      fetchCapitalData();
    }
  }, [userID, tradeAccountID, trades, showOverallPerformance]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userCreatedAt || userCapital === null) return;
      setLoading(true);

      // NEW: Check cache first
      const tradesString = JSON.stringify(trades.map(t => ({
        id: t.id,
        date: t.date,
        entryTime: t.entryTime,
        exitDate: t.exitDate,
        exitTime: t.exitTime,
        returns: t.returns,
        status: t.status,
        entry: t.entry,
        sl: t.sl,
        tp: t.tp,
        tradeConfidence: t.tradeConfidence,
        fxbTradeScore: t.fxbTradeScore,
        checklistResponses: t.checklistResponses
      })));
      const newTradesHash = md5(tradesString);
      const cached = loadCachedData();

      if (cached && cached.tradesHash === newTradesHash) {
        console.log('Using cached report');
        setReport(cached.report);
        setLastMonthScore(cached.lastMonthScore);
        setLoading(false);
        return;
      }

      try {
        let closedTrades, missedTrades;
        if (showOverallPerformance) {
          const tradeAccountsSnapshot = await getDocs(collection(db, 'Users', userID, 'tradeAccounts'));
          const allClosedTrades = [];
          const allMissedTrades = [];
          for (const accountDoc of tradeAccountsSnapshot.docs) {
            const accountID = accountDoc.id;
            const closedQuery = query(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'trades'), where('status', '==', 'Closed'));
            const missedQuery = query(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'trades'), where('status', '==', 'Missed'));
            const [closedSnap, missedSnap] = await Promise.all([getDocs(closedQuery), getDocs(missedQuery)]);
            const closedTradeDocs = closedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const missedTradeDocs = missedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Fetch trade replays for each closed trade
            for (const trade of closedTradeDocs) {
              const replaysQuery = query(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'trades', trade.id, 'tradeReplays'));
              const replaysSnap = await getDocs(replaysQuery);
              trade.tradeReplays = replaysSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }
            allClosedTrades.push(...closedTradeDocs);
            allMissedTrades.push(...missedTradeDocs);
          }
          closedTrades = allClosedTrades;
          missedTrades = allMissedTrades;
        } else {
          closedTrades = trades.filter(trade => trade.status === 'Closed').map(trade => ({ id: trade.id, ...trade }));
          missedTrades = trades.filter(trade => trade.status === 'Missed').map(trade => ({ id: trade.id, ...trade }));
          
          // Fetch trade replays for each closed trade in the selected account
          for (const trade of closedTrades) {
            const replaysQuery = query(collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'trades', trade.id, 'tradeReplays'));
            const replaysSnap = await getDocs(replaysQuery);
            trade.tradeReplays = replaysSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          }
        }
  
        const processedReport = await processReport(closedTrades, missedTrades);
  
        const scoresPath = showOverallPerformance
          ? collection(db, 'Users', userID, 'monthlyScores')
          : collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'monthlyScores');
        const scoreSnapshot = await getDocs(query(scoresPath));
        const scores = scoreSnapshot.docs.map(doc => {
          const data = doc.data();
          return { ...data, score: typeof data.score === 'string' ? parseFloat(data.score) : data.score };
        });
        const lastMonthScore = scores.find(score =>
          score.month === `${processedReport.lastMonth.getFullYear()}-${processedReport.lastMonth.getMonth() + 1}`
        );
  
        await saveScoreToFirebase(processedReport);
        setReport(processedReport);
        setLastMonthScore(lastMonthScore ? lastMonthScore.score : null);

        // NEW: Save to cache
        saveCachedData({
          report: processedReport,
          lastMonthScore: lastMonthScore ? lastMonthScore.score : null,
          tradesHash: newTradesHash
        });

        setLoading(false);
      } catch (error) {
        console.error('Error processing trades data:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [userID, userCreatedAt, userCapital, trades, tradeAccountID, showOverallPerformance]);

  // Rest of the code remains unchanged
  useEffect(() => {
    onTogglePerformanceView(showOverallPerformance);
  }, [showOverallPerformance, onTogglePerformanceView]);

  useEffect(() => {
    document.documentElement.style.setProperty('--mui-table-header-bg-color', theme.palette.background.default);
    document.documentElement.style.setProperty('--mui-table-header-text-color', theme.palette.text.primary);
    document.documentElement.style.setProperty('--mui-table-cell-bg-color', theme.palette.background.paper);
    document.documentElement.style.setProperty('--mui-table-cell-text-color', theme.palette.text.primary);
  }, [theme]);

  useLayoutEffect(() => {
    if (tableRef.current && nonTableContentRef.current) {
      const cardHeight = 850;
      const minTableFontSize = windowWidth < 600 ? 0.6 : 0.7;
      const tableRows = (report.hasFxbScore ? 10 : 9) + (report.totalReplays ? 1 : 0);
      const safetyMargin = 10;

      const nonTableHeight = nonTableContentRef.current.offsetHeight;
      if (chartType === 'table') {
        const headerHeight = tableRef.current.querySelector('thead')?.offsetHeight || 40;
        const availableTableHeight = cardHeight - nonTableHeight - safetyMargin - headerHeight;
        const maxRowHeight = availableTableHeight / (tableRows - 1); // Exclude header row
        let tableFontSizeRem = windowWidth < 600 ? 0.75 : 0.875;
        tableRef.current.style.fontSize = `${tableFontSizeRem}rem`;
        let tableHeight = tableRef.current.offsetHeight;
        let rowHeight = tableHeight / tableRows;

        while (rowHeight > maxRowHeight && tableFontSizeRem > minTableFontSize) {
          tableFontSizeRem -= 0.025;
          tableRef.current.style.fontSize = `${tableFontSizeRem}rem`;
          tableRef.current.style.display = 'none';
          tableRef.current.offsetHeight;
          tableRef.current.style.display = 'table';
          tableHeight = tableRef.current.offsetHeight;
          rowHeight = tableHeight / tableRows;
        }
        tableFontSizeRem = Math.max(tableFontSizeRem, minTableFontSize);
        setTableFontSize(`${tableFontSizeRem}rem`);
        setTableRowHeight(`${maxRowHeight}px`);
      }
    }
  }, [report.hasFxbScore, report.totalReplays, windowWidth, chartType]);

  const determineBestTradeSession = (trades) => {
    const tradeCountBySession = { Asia: 0, London: 0, NewYork: 0 };
    trades.forEach(trade => {
      const entryHour = new Date(`${trade.date} ${trade.entryTime}`).getHours();
      const session = entryHour >= 0 && entryHour < 8 ? 'Asia' : entryHour >= 8 && entryHour < 16 ? 'London' : 'NewYork';
      tradeCountBySession[session]++;
    });
    return Object.keys(tradeCountBySession).reduce((a, b) => tradeCountBySession[a] > tradeCountBySession[b] ? a : b);
  };

  const processReport = async (trades, missedTrades) => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);

    const thisMonthTrades = trades.filter(trade => {
      const tradeDate = new Date(trade.date);
      return tradeDate >= lastMonth && tradeDate < thisMonth;
    });
    console.log('thisMonthTrades:', thisMonthTrades.map(t => ({ id: t.id, date: t.date })));

    const lastMonthTrades = trades.filter(trade => {
      const tradeDate = new Date(trade.date);
      return tradeDate >= twoMonthsAgo && tradeDate < lastMonth;
    });
    const thisMonthMissedTrades = missedTrades.filter(trade => {
      const tradeDate = new Date(trade.date);
      return tradeDate >= lastMonth && tradeDate < thisMonth;
    });

    const thisMonthReport = await generateMonthlyReport(thisMonthTrades, lastMonth, thisMonth);
    const lastMonthReport = await generateMonthlyReport(lastMonthTrades, twoMonthsAgo, lastMonth);

    const score = calculateScore(thisMonthReport);
    const lastMonthScore = calculateScore(lastMonthReport);
    const effectiveScore = thisMonthTrades.length > 0 ? score : lastMonthScore;

    const feedback = generateFeedback(score, lastMonthScore, thisMonthReport, lastMonthReport, thisMonthMissedTrades, lastMonth, thisMonthTrades, userCreatedAt);

    return {
      ...thisMonthReport,
      score: effectiveScore,
      feedback,
      lastMonth,
      thisMonth,
      missedTrades: thisMonthMissedTrades,
      twoMonthsAgoReport: lastMonthReport,
      hasFxbScore: thisMonthTrades.some(trade => trade.fxbTradeScore),
    };
  };

  const isScalpingStrategy = (trades, winRate, riskRewardRatio, averageHoldTime) => {
    const isHighWinRate = winRate >= 65;
    const isShortHoldTime = averageHoldTime / (1000 * 60) <= 30;
    const isLowerRR = riskRewardRatio < 1.5;
    const isHighFrequency = trades.length >= 50;
    return isHighWinRate && isShortHoldTime && (isLowerRR || isHighFrequency);
  };

  const isRiskyScalping = (trades, winRate, riskRewardRatio, averageHoldTime) => {
    const isShortHoldTime = averageHoldTime / (1000 * 60) <= 30;
    const isLowerRR = riskRewardRatio < 1.5;
    const isRiskyWinRate = winRate < 55;
    return isShortHoldTime && isLowerRR && isRiskyWinRate;
  };

  const getScalpingFeedback = (report, trades) => {
    const { winRate, profitFactor, totalProfit, averageHoldTime, riskRewardRatio } = report;
    if (isRiskyScalping(trades, winRate, riskRewardRatio, averageHoldTime)) {
      let feedback = `Warning: Your trading pattern shows attempted scalping with ${Math.round(averageHoldTime / (1000 * 60))} minute holds and low RR (${(riskRewardRatio || 0).toFixed(2)}). A ${(winRate || 0).toFixed(1)}% win rate is too low for sustainable scalping. `;
      if ((totalProfit || 0) > 0 && (profitFactor || 0) > 1) {
        feedback += `While you were profitable this month ($${(totalProfit || 0).toFixed(0)}), this approach carries high risk long-term. Consider improving execution for higher win rate or adjusting to higher RR strategy. `;
      } else {
        feedback += `Consider improving execution or switching to a higher win rate strategy. `;
      }
      return feedback;
    }
    if (!isScalpingStrategy(trades, winRate, riskRewardRatio, averageHoldTime)) return '';
    let feedback = `Your trading shows an effective scalping approach with a ${(winRate || 0).toFixed(1)}% win rate over ${trades.length} trades. `;
    if ((profitFactor || 0) > 1.5 && (winRate || 0) > 75) {
      feedback += `Excellent execution with $${(totalProfit || 0).toFixed(0)} profit and ${Math.round(averageHoldTime / (1000 * 60))} minute holds. Your high win rate compensates well for the typical lower RR ratios in your scalping. `;
    } else if ((profitFactor || 0) > 1) {
      feedback += `Good results with room to optimize. Consider fine-tuning your exits to improve profit factor while maintaining your strong win rate. `;
    }
    return feedback;
  };

  const generateMonthlyReport = async (trades, startDate, endDate) => {
    const validTrades = trades.filter(trade =>
      trade.returns !== 'N/A' && !isNaN(trade.returns) &&
      trade.entry !== 'N/A' && !isNaN(trade.entry) &&
      trade.sl !== 'N/A' && !isNaN(trade.sl) &&
      trade.tp !== 'N/A' && !isNaN(trade.tp)
    );
    const totalTrades = trades.length;
    const winningTrades = trades.filter(trade => trade.returns > 0);
    const losingTrades = trades.filter(trade => trade.returns <= 0);
    const totalProfit = trades.reduce((sum, trade) => sum + parseFloat(trade.returns || 0), 0);
    const averageProfit = totalTrades ? totalProfit / totalTrades : 0;
    const winRate = totalTrades ? (winningTrades.length / totalTrades) * 100 : 0;
    const grossProfit = winningTrades.reduce((sum, trade) => sum + parseFloat(trade.returns || 0), 0);
    const grossLoss = losingTrades.reduce((sum, trade) => sum + parseFloat(trade.returns || 0), 0);
    const profitFactor = grossLoss !== 0 ? (grossProfit / Math.abs(grossLoss)) : 0;
    const totalRisk = trades.reduce((sum, trade) => sum + Math.abs(parseFloat(trade.entry) - parseFloat(trade.sl)), 0);
    const totalReward = trades.reduce((sum, trade) => sum + Math.abs(parseFloat(trade.tp) - parseFloat(trade.entry)), 0);
    const riskRewardRatio = totalRisk ? (totalReward / totalRisk) : 0;
    const tradeEfficiency = totalRisk ? (totalProfit / totalRisk) : 0;

    const totalHoldTime = trades.reduce((sum, trade) => {
      const entryTime = new Date(`${trade.date} ${trade.entryTime}`);
      const exitTime = new Date(`${trade.exitDate} ${trade.exitTime}`);
      return sum + (exitTime - entryTime);
    }, 0);
    const averageHoldTime = totalTrades ? (totalHoldTime / totalTrades) : 0;

    const actualRR = totalRisk ? (totalReward / totalRisk) : 0;
    const targetedRR = totalTrades
      ? validTrades.reduce((sum, trade) => {
          const tradeRR = Math.abs(parseFloat(trade.tp) - parseFloat(trade.entry)) / Math.abs(parseFloat(trade.entry) - parseFloat(trade.sl));
          return sum + (isNaN(tradeRR) ? 0 : tradeRR);
        }, 0) / totalTrades
      : 0;

    const highConfidenceTrades = trades.filter(trade => (trade.tradeConfidence || 'Medium') === 'High');
    const highConfidenceWinRate = highConfidenceTrades.length
      ? (highConfidenceTrades.filter(trade => trade.returns > 0).length / highConfidenceTrades.length) * 100
      : 0;

    const fxbTrades = trades.filter(trade => trade.fxbTradeScore && trade.checklistResponses);
    const planAdherence = fxbTrades.length
      ? (fxbTrades.filter(trade => {
          const score = parseInt(trade.fxbTradeScore.split('/')[0], 10);
          return score >= 80;
        }).length / fxbTrades.length) * 100
      : null;

    // Fetch replays directly from Firestore for the specified period
    const fetchReplays = async () => {
      let allReplays = [];
      if (showOverallPerformance) {
        const tradeAccountsSnapshot = await getDocs(collection(db, 'Users', userID, 'tradeAccounts'));
        for (const accountDoc of tradeAccountsSnapshot.docs) {
          const accountID = accountDoc.id;
          const tradesSnapshot = await getDocs(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'trades'));
          for (const tradeDoc of tradesSnapshot.docs) {
            const tradeData = tradeDoc.data();
            const tradeDate = new Date(tradeData.date);
            const replaysQuery = query(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'trades', tradeDoc.id, 'tradeReplays'));
            const replaysSnap = await getDocs(replaysQuery);
            const replays = replaysSnap.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data(), 
              tradeDate: tradeData.date
            }));
            allReplays.push(...replays);
          }
        }
      } else {
        const tradesSnapshot = await getDocs(collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'trades'));
        for (const tradeDoc of tradesSnapshot.docs) {
          const tradeData = tradeDoc.data();
          const tradeDate = new Date(tradeData.date);
          const replaysQuery = query(collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'trades', tradeDoc.id, 'tradeReplays'));
          const replaysSnap = await getDocs(replaysQuery);
          const replays = replaysSnap.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(), 
            tradeDate: tradeData.date
          }));
          allReplays.push(...replays);
        }
      }
      return allReplays.filter(replay => {
        if (!replay.tradeDate) {
          console.warn('Replay missing tradeDate:', replay.id);
          return false;
        }
        const replayDate = new Date(replay.tradeDate);
        const isWithinRange = replayDate >= startDate && replayDate < endDate;
        if (!isWithinRange) {
          console.warn('Replay outside date range:', replay.id, replay.tradeDate, replay.timestamp);
        }
        console.log('Replay:', replay.id, 'tradeDate:', replay.tradeDate, 'timestamp:', replay.timestamp);
        return isWithinRange;
      });
    };

    let replays = [];
    try {
      replays = await fetchReplays();
    } catch (error) {
      console.error('Error fetching replays:', error);
    }

    const totalReplays = replays.length;

    console.log('Replays:', replays);

    let replayMetrics = {
      totalScore: 0,
      entryScore: 0,
      exitScore: 0,
      rrScore: 0,
      beScore: 0,
      volatilityScore: 0,
      durationScore: 0,
      count: 0,
    };

    if (totalReplays > 0) {
      replayMetrics = replays.reduce((acc, replay) => {
        const entryScore = parseFloat(replay.analysis?.entry?.score) || 0;
        const exitScore = parseFloat(replay.analysis?.exit?.score) || 0;
        const rrScore = parseFloat(replay.analysis?.rr?.score) || 0;
        const beScore = parseFloat(replay.analysis?.be?.score) || 0;
        const volatilityScore = parseFloat(replay.analysis?.volatility?.score) || 0;
        const durationScore = parseFloat(replay.analysis?.duration?.score) || 0;

        const totalScore = parseFloat(replay.score) || (
          entryScore + exitScore + rrScore + beScore + volatilityScore + durationScore
        );

        if (
          isNaN(totalScore) || isNaN(entryScore) || isNaN(exitScore) || 
          isNaN(rrScore) || isNaN(beScore) || isNaN(volatilityScore) || 
          isNaN(durationScore)
        ) {
          console.warn('Invalid replay scores detected:', replay);
          return acc;
        }

        return {
          totalScore: acc.totalScore + totalScore,
          entryScore: acc.entryScore + entryScore,
          exitScore: acc.exitScore + exitScore,
          rrScore: acc.rrScore + rrScore,
          beScore: acc.beScore + beScore,
          volatilityScore: acc.volatilityScore + volatilityScore,
          durationScore: acc.durationScore + durationScore,
          count: acc.count + 1,
        };
      }, replayMetrics);

      console.log('Replay Metrics (before averaging):', replayMetrics);

      replayMetrics.averageTotalScore = replayMetrics.count
        ? (replayMetrics.totalScore / replayMetrics.count).toFixed(2)
        : '0.00';
      replayMetrics.averageEntryScore = replayMetrics.count
        ? (replayMetrics.entryScore / replayMetrics.count).toFixed(2)
        : '0.00';
      replayMetrics.averageExitScore = replayMetrics.count
        ? (replayMetrics.exitScore / replayMetrics.count).toFixed(2)
        : '0.00';
      replayMetrics.averageRRScore = replayMetrics.count
        ? (replayMetrics.rrScore / replayMetrics.count).toFixed(2)
        : '0.00';
      replayMetrics.averageBEScore = replayMetrics.count
        ? (replayMetrics.beScore / replayMetrics.count).toFixed(2)
        : '0.00';
      replayMetrics.averageVolatilityScore = replayMetrics.count
        ? (replayMetrics.volatilityScore / replayMetrics.count).toFixed(2)
        : '0.00';
      replayMetrics.averageDurationScore = replayMetrics.count
        ? (replayMetrics.durationScore / replayMetrics.count).toFixed(2)
        : '0.00';
    }

    console.log('Final Replay Metrics:', replayMetrics);

    return {
      totalTrades,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      totalProfit,
      averageProfit,
      winRate,
      profitFactor,
      totalRisk,
      riskRewardRatio,
      tradeEfficiency,
      averageHoldTime,
      actualRR,
      targetedRR,
      bestTradeSession: determineBestTradeSession(trades),
      missedTrades: trades.filter(trade => trade.status === 'Missed'),
      highConfidenceWinRate,
      planAdherence,
      totalReplays,
      replayMetrics,
    };
  };

  const calculateScore = ({ winRate, profitFactor, averageProfit, totalProfit, riskRewardRatio, tradeEfficiency, highConfidenceWinRate, planAdherence }) => {
    const isScalper = (winRate || 0) >= 75 && (riskRewardRatio || 0) < 1.5;
    const weights = isScalper
      ? { winRate: 0.25, profitFactor: 0.20, rr: 0.10, efficiency: 0.15, totalProfit: 0.10, avgProfit: 0.10, highConfidence: 0.10 }
      : { winRate: 0.20, profitFactor: 0.20, rr: 0.20, efficiency: 0.15, totalProfit: 0.15, avgProfit: 0.10, highConfidence: 0.10 };

    const normalizedWinRate = ((winRate || 0) / 100) * 10;
    const normalizedProfitFactor = Math.min((profitFactor || 0), 5) * 2;
    const normalizedRR = isScalper
      ? Math.max(Math.min((riskRewardRatio || 0) * 2, 2), 0) * 5
      : Math.max(Math.min((riskRewardRatio || 0), 3), 0) * 3.33;
    const normalizedEfficiency = Math.max(Math.min((tradeEfficiency || 0) / 10, 10), 0);
    const normalizedTotalProfit = userCapital
      ? Math.max(Math.min(((totalProfit || 0) / userCapital) * 10, 10), 0)
      : Math.min((totalProfit || 0) / 1000, 10);
    const normalizedAvgProfit = userCapital
      ? Math.max(Math.min(((averageProfit || 0) / (userCapital / 20)) * 10, 10), 0)
      : Math.min((averageProfit || 0) / 100, 10);
    const normalizedHighConfidenceWinRate = ((highConfidenceWinRate || 0) / 100) * 10;

    let score = (
      (normalizedWinRate * weights.winRate) +
      (normalizedProfitFactor * weights.profitFactor) +
      (normalizedRR * weights.rr) +
      (normalizedEfficiency * weights.efficiency) +
      (normalizedTotalProfit * weights.totalProfit) +
      (normalizedAvgProfit * weights.avgProfit) +
      (normalizedHighConfidenceWinRate * weights.highConfidence)
    );

    if (planAdherence !== null) {
      const normalizedPlanAdherence = ((planAdherence || 0) / 100) * 10;
      score = (score * 0.9) + (normalizedPlanAdherence * 0.1);
    }

    return parseFloat(score.toFixed(1));
  };

  const saveScoreToFirebase = async (report) => {
    const scoresCollection = showOverallPerformance
      ? collection(db, 'Users', userID, 'monthlyScores')
      : collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'monthlyScores');
    const previousMonth = `${report.lastMonth.getFullYear()}-${report.lastMonth.getMonth() + 1}`;
    const docRef = doc(scoresCollection, previousMonth);
  
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        await updateDoc(docRef, {
          score: report.score,
          averageHoldTime: report.averageHoldTime || 0,
          actualRR: report.actualRR || 0,
          targetedRR: report.targetedRR || 0,
          bestTradeSession: report.bestTradeSession || 'N/A',
          highConfidenceWinRate: report.highConfidenceWinRate || 0,
          planAdherence: report.performance || null,
          totalReplays: report.totalReplays || 0,
          replays: report.replayMetrics.averageTotalScore || 0,
          averageReplayScore: report.score,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await setDoc(docRef, {
          month: previousMonth,
          score: report.score,
          totalTrades: report.totalTrades || 0,
          winningTrades: report.winningTrades || 0,
          losingTrades: report.losingTrades || 0,
          totalProfit: report.totalProfit || 0,
          averageProfit: report.averageProfit || 0,
          winRate: report.winRate || 0,
          profitFactor: report.profitFactor || 0,
          missedTrades: (report.missedTrades || []).length || 0,
          riskRewardRatio: report.riskRewardRatio || 0,
          averageHoldTime: report.averageHoldTime || 0,
          actualRR: report.actualRR || 0,
          targetedRR: report.targetedRR || 0,
          bestTradeSession: report.bestTradeSession || 'N/A',
          highConfidenceWinRate: report.highConfidenceWinRate || 0,
          planAdherence: report.planAdherence || null,
          totalReplays: report.totalReplays || 0,
          replays: report.replayMetrics?.averageTotalScore || 0,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error saving score to Firebase:', error);
    }
  };

  const generateFeedback = (score, lastMonthScore, thisMonthReport, lastMonthReport, thisMonthMissedTrades, lastMonth, trades, createdAt) => {
    const { totalTrades, highConfidenceWinRate, planAdherence, totalReplays, replayMetrics } = thisMonthReport;
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const reportMonth = monthNames[lastMonth.getMonth()];
    const userCreatedAt = new Date(createdAt);
    const reportMonthEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 1);

    const numericScore = typeof score === 'string' ? parseFloat(score) : score;
    const numericLastMonthScore = typeof lastMonthScore === 'string' ? parseFloat(lastMonthScore) : lastMonthScore;

    if (userCreatedAt >= reportMonthEnd) {
      return `Welcome to FX Bull! Since this is your first month${showOverallPerformance ? '' : ' with this account'}, we'll have a performance report ready for next month. Stay tuned!`;
    }

    if (userCreatedAt > new Date(lastMonth.getFullYear(), lastMonth.getMonth() - 1, 1)) {
      return `Since this marks the beginning of your journaling journey${showOverallPerformance ? '' : ' for this account'}, we'll provide a comparison to assess your development. Below you will find your trade report data for ${reportMonth}`;
    }

    if (totalTrades === 0) {
      return `It seems like you didn't journal any trades${showOverallPerformance ? '' : ' for this account'} in ${reportMonth}. Journaling your trade is crucial for your trader's growth. Without this valuable data we can't provide feedback or insights to help you improve your trading performance. We encourage you to start documenting your trades to track your progress and enhance your trading skills. Since we also cannot provide you with an updated monthly score, we'll keep it the same as last month, but this data hasn't been logged towards your progress.`;
    }

    const { winRate, profitFactor, riskRewardRatio, averageHoldTime, actualRR, targetedRR } = thisMonthReport;

    const highConfidenceTrades = trades.filter(trade => (trade.tradeConfidence || 'Medium') === 'High');
    const mediumConfidenceTrades = trades.filter(trade => (trade.tradeConfidence || 'Medium') === 'Medium');
    const lowConfidenceTrades = trades.filter(trade => (trade.tradeConfidence || 'Medium') === 'Low');

    const highConfidenceWinRateCalc = highConfidenceTrades.length ? (highConfidenceTrades.filter(trade => trade.returns > 0).length / highConfidenceTrades.length * 100) : 0;
    const mediumConfidenceWinRate = mediumConfidenceTrades.length ? (mediumConfidenceTrades.filter(trade => trade.returns > 0).length / mediumConfidenceTrades.length * 100) : 0;
    const lowConfidenceWinRate = lowConfidenceTrades.length ? (lowConfidenceTrades.filter(trade => trade.returns > 0).length / lowConfidenceTrades.length * 100) : 0;

    let performanceComparison = '';
    if (!lastMonthReport || isNaN(numericLastMonthScore)) {
      performanceComparison = `Since this marks the beginning of your journaling journey${showOverallPerformance ? '' : ' for this account'}, we'll provide a comparison next month to assess your development.`;
    } else {
      const scoreDifference = numericScore - numericLastMonthScore;
      if (scoreDifference > 0) {
        performanceComparison = `Overall, you improved by ${scoreDifference.toFixed(1)} points this month compared to the previous month.`;
      } else if (scoreDifference < 0) {
        performanceComparison = `Overall, you declined by ${Math.abs(scoreDifference).toFixed(1)} points this month compared to the previous month.`;
      } else {
        performanceComparison = `Overall, your performance was consistent with the previous month.`;
      }
    }

    let performanceFeedback = '';
    if (numericScore >= 9) {
      performanceFeedback = `Excellent performance! You had an outstanding month in ${reportMonth}${showOverallPerformance ? '' : ' with this account'}. This is a great achievement and shows that you are an elite trader. Keep up the good work!`;
    } else if (numericScore >= 7) {
      performanceFeedback = `Great job! You had a strong performance in ${reportMonth}${showOverallPerformance ? '' : ' with this account'}. Keep up the good work!`;
    } else if (numericScore >= 5) {
      performanceFeedback = `Good effort! You did well in ${reportMonth}${showOverallPerformance ? '' : ' with this account'} but there is room for improvement. Knowledge is power, so keep learning and growing.`;
    } else if (numericScore >= 3) {
      performanceFeedback = `You had a fair performance${showOverallPerformance ? '' : ' with this account'}. Consider reviewing your trades in ${reportMonth} to improve. Remember consistency is key to success.`;
    } else {
      performanceFeedback = `You had a poor performance in ${reportMonth}${showOverallPerformance ? '' : ' with this account'}. A detailed review of your trading strategy is highly recommended. This may have been a challenging month, but it's important to learn from your trades and make adjustments for the future.`;
    }

    if (totalTrades > 0) {
      if (highConfidenceTrades.length > 0) {
        performanceFeedback += ` Your high-confidence trades had a win rate of ${highConfidenceWinRateCalc.toFixed(1)}%.`;
        if (highConfidenceWinRateCalc > 60) {
          performanceFeedback += ` This strong performance suggests your high-confidence setups are reliable. Focus on these opportunities to maximize returns.`;
        } else if (highConfidenceWinRateCalc < 40) {
          performanceFeedback += ` Your high-confidence trades underperformed. Review these setups to identify emotional or strategic biases affecting your decisions.`;
        }
      }
      if (mediumConfidenceTrades.length > 0) {
        performanceFeedback += ` Your medium-confidence trades had a win rate of ${mediumConfidenceWinRate.toFixed(1)}%.`;
      }
      if (lowConfidenceTrades.length > 0) {
        performanceFeedback += ` Your low-confidence trades had a win rate of ${lowConfidenceWinRate.toFixed(1)}%.`;
        if (lowConfidenceWinRate > highConfidenceWinRateCalc && lowConfidenceWinRate > 50) {
          performanceFeedback += ` Surprisingly, your low-confidence trades outperformed others. This may indicate overcaution in high-confidence setups or strong intuition in uncertain trades. Refine your confidence assessments.`;
        }
      }

      if (highConfidenceWinRateCalc < 50 && mediumConfidenceWinRate < 50 && lowConfidenceWinRate < 50) {
        performanceFeedback += ` Your trades across all confidence levels are underperforming. Emotional factors may be impacting your decisions. Consider journaling your mindset to improve trade selection.`;
      }
    }

    if (planAdherence !== null) {
      performanceFeedback += ` ${planAdherence.toFixed(1)}% of your trades adhered to your trading check list (FX Bullsheet score ≥ 80/100).`;
      if (planAdherence >= 80) {
        performanceFeedback += ` Excellent discipline! Most trades followed your check list, contributing to consistent performance.`;
      } else if (planAdherence >= 50) {
        performanceFeedback += ` You adhered to your check list in over half your trades, but there’s room to improve. Review trades below 80/100 to identify deviations.`;
      } else {
        performanceFeedback += ` Many trades deviated from your check list. Consider reviewing your checklist responses to strengthen adherence.`;
      }
    }

    if (winRate < 50 && (profitFactor || 0) > 1) {
      performanceFeedback += ` Although your win rate was below 50%, your positive profit factor indicates profitable trades. This suggests you have effective risk management.`;
    } else if ((winRate || 0) >= 50 && (profitFactor || 0) < 1) {
      performanceFeedback += ` Despite a win rate above 50%, your negative profit factor indicates losses. This may mean you are risking too much per trade and setting your take profits too low, consider reviewing your risk management.`;
    } else if ((winRate || 0) >= 50 && (profitFactor || 0) > 1) {
      performanceFeedback += ` You have a win rate above 50% and a positive profit factor. This indicates good risk management and a good trading strategy!`;
    }

    if ((thisMonthMissedTrades || []).filter(trade => trade.returns > 0).length > 0) {
      performanceFeedback += ` You missed ${(thisMonthMissedTrades || []).filter(trade => trade.returns > 0).length} positive trade(s). This may be due to hesitation - we recommend you analyze your missed trades in ${reportMonth}!`;
    }

    const lastMonthTotalTrades = lastMonthReport?.totalTrades || 0;
    if (totalTrades > lastMonthTotalTrades) {
      performanceFeedback += ` You made more trades in ${reportMonth} (${totalTrades}) compared to the previous month (${lastMonthTotalTrades})`;
    } else if (totalTrades < lastMonthTotalTrades) {
      performanceFeedback += ` You made fewer trades in ${reportMonth} (${totalTrades}) compared to the previous month (${lastMonthTotalTrades})`;
    }

    if ((riskRewardRatio || 0) < 1.99) {
      performanceFeedback += ` Your average RR Ratio in ${reportMonth} is low, suggesting that you may not be adequately rewarded for the risk taken in your trades. Consider revising your strategy to improve your RR Ratio.`;
    } else if ((riskRewardRatio || 0) >= 1.99 && (riskRewardRatio || 0) < 2.99) {
      performanceFeedback += ` Your average RR Ratio in ${reportMonth} is at an acceptable range, but there is room for improvement to optimize your returns.`;
    } else if ((riskRewardRatio || 0) >= 2.99) {
      performanceFeedback += ` Your average RR Ratio in ${reportMonth} exceeds the desirable 1:3 ratio, showing you are effectively managing risk and achieving good returns.`;
    }

    if (averageHoldTime !== null && !isNaN(averageHoldTime)) {
      performanceFeedback += ` Your average hold time in ${reportMonth} was ${Math.round((averageHoldTime || 0) / (1000 * 60 * 60))} hours.`;
    }

    if (actualRR !== null) {
      performanceFeedback += ` Your actual RR was ${(actualRR || 0).toFixed(2)}, compared to a targeted RR of ${(targetedRR || 0).toFixed(2)}`;
    }
    if ((actualRR || 0) < (targetedRR || 0)) {
      performanceFeedback += ` Your actual RR ratio is below your target. Consider reviewing your exit strategy to capture more of your trade potential.`;
    }

    if (totalReplays > 0) {
      performanceFeedback += ` You conducted ${totalReplays} trade replay${totalReplays === 1 ? '' : 's'} in ${reportMonth}. Your average replay score was ${replayMetrics.averageTotalScore} out of 10. `;
      if (replayMetrics.averageTotalScore >= 8) {
        performanceFeedback += `Excellent replay performance! Your trade replays show strong execution across entry, exit, and risk management. `;
      } else if (replayMetrics.averageTotalScore >= 6) {
        performanceFeedback += `Good replay performance, with room for improvement. `;
      } else {
        performanceFeedback += `Your replay scores suggest significant areas for improvement. `;
      }

      // Define maximum scores for each metric
      const maxScores = {
        rr: 2,
        exit: 2,
        entry: 2,
        be: 2,
        volatility: 1,
        duration: 1,
      };

      if (replayMetrics.averageRRScore < maxScores.rr) {
        performanceFeedback += `Your risk-reward ratio (average score: ${replayMetrics.averageRRScore}/${maxScores.rr}) indicates you're missing higher RRs. Consider reviewing your trading plan to optimize your risk-reward setups. `;
      }
      if (replayMetrics.averageExitScore < maxScores.exit) {
        performanceFeedback += `Your exits (average score: ${replayMetrics.averageExitScore}/${maxScores.exit}) could be improved. Consider using trailing stops or holding positions longer to capture more of the price movement. `;
      }
      if (replayMetrics.averageEntryScore < maxScores.entry) {
        performanceFeedback += `Your entries (average score: ${replayMetrics.averageEntryScore}/${maxScores.entry}) show room for improvement. Focus on timing your entries closer to optimal price levels. `;
      }
      if (replayMetrics.averageBEScore < maxScores.be) {
        performanceFeedback += `Your break-even strategy (average score: ${replayMetrics.averageBEScore}/${maxScores.be}) needs attention. Review when and how you apply break-even adjustments. `;
      }
      if (replayMetrics.averageVolatilityScore < maxScores.volatility) {
        performanceFeedback += `Your volatility management (average score: ${replayMetrics.averageVolatilityScore}/${maxScores.volatility}) is suboptimal. Ensure your stop-loss aligns with market volatility (e.g., ATR). `;
      }
      if (replayMetrics.averageDurationScore < maxScores.duration) {
        performanceFeedback += `Your trade duration (average score: ${replayMetrics.averageDurationScore}/${maxScores.duration}) indicates holding trades too long or too short. Review optimal exit timing based on price action. `;
      }
    } else {
      performanceFeedback += ` You did not conduct any trade replays in ${reportMonth}. Replaying trades can provide valuable insights into your performance. Consider using the trade replay tool to analyze your trades and improve your strategy.`;
    }

    const bestTradeSession = determineBestTradeSession(trades);
    const tradesInBestSession = trades.filter(trade => determineBestTradeSession([trade]) === bestTradeSession).length;
    const tradesOutsideBestSession = totalTrades - tradesInBestSession;
    performanceFeedback += ` Your best trading session was during the ${bestTradeSession} session. You made ${tradesInBestSession} trades within this session and ${tradesOutsideBestSession} trades outside of it.`;

    if (tradesInBestSession > tradesOutsideBestSession) {
      performanceFeedback += ` You have successfully focused your trading during this session, leading to better performance. Keep up the good work!`;
    }
    if (tradesOutsideBestSession > tradesInBestSession) {
      performanceFeedback += ` Since you have better performance during the ${bestTradeSession} session, try to concentrate your trading activities during this time to maximize your success.`;
    }
    if ((thisMonthMissedTrades || []).filter(trade => trade.returns > 0).length > 0) {
      performanceFeedback += ` To avoid missing positive trades, ensure you stick to your trading plan and avoid hesitation. Regularly review your missed trades to understand the reasons behind the misses and improve your decision-making process.`;
    }

    const scalpingFeedback = getScalpingFeedback(thisMonthReport, trades);
    if (scalpingFeedback) performanceFeedback += ' ' + scalpingFeedback;

    return `${performanceFeedback} ${performanceComparison}`;
  };

  const getPreviousMonthName = () => {
    const now = new Date();
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return monthNames[previousMonth.getMonth()];
  };

  const normalizeMetric = (value, maxValue) => {
    return Math.min(((value || 0) / maxValue) * 100, 100);
  };

  const radarData = {
    labels: [
      'Win Rate (%)',
      'Total Profit ($)',
      'Profit Factor',
      'Overall RR',
      'High Confidence Win Rate (%)',
      ...(report.hasFxbScore ? ['Check List Adherence (%)'] : []),
      ...(report.totalReplays ? ['Average Replay Score (%)'] : []),
    ],
    datasets: [
      {
        label: 'Last Month',
        data: [
          normalizeMetric(report.winRate || 0, 100),
          normalizeMetric(report.totalProfit || 0, 5000),
          normalizeMetric(report.profitFactor || 0, 5),
          normalizeMetric(report.riskRewardRatio || 0, 5),
          normalizeMetric(report.highConfidenceWinRate || 0, 100),
          ...(report.hasFxbScore ? [normalizeMetric(report.planAdherence || 0, 100)] : []),
          ...(report.totalReplays ? [normalizeMetric(report.replayMetrics?.averageTotalScore || 0, 10)] : []),
        ],
        backgroundColor: darkMode ? 'rgba(54, 162, 235, 0.2)' : 'rgba(75, 192, 192, 0.2)',
        borderColor: darkMode ? 'rgba(54, 162, 235, 1)' : 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Previous Month',
        data: [
          normalizeMetric(report.twoMonthsAgoReport?.winRate || 0, 100),
          normalizeMetric(report.twoMonthsAgoReport?.totalProfit || 0, 5000),
          normalizeMetric(report.twoMonthsAgoReport?.profitFactor || 0, 5),
          normalizeMetric(report.twoMonthsAgoReport?.riskRewardRatio || 0, 5),
          normalizeMetric(report.twoMonthsAgoReport?.highConfidenceWinRate || 0, 100),
          ...(report.hasFxbScore ? [normalizeMetric(report.twoMonthsAgoReport?.planAdherence || 0, 100)] : []),
          ...(report.twoMonthsAgoReport?.totalReplays ? [normalizeMetric(report.twoMonthsAgoReport?.replayMetrics?.averageTotalScore || 0, 10)] : []),
        ],
        backgroundColor: darkMode ? 'rgba(255, 99, 132, 0.2)' : 'rgba(255, 99, 132, 0.2)',
        borderColor: darkMode ? 'rgba(255, 99, 132, 1)' : 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      },
    ],
  };

  const radarOptions = {
    scales: {
      r: {
        angleLines: { display: true, color: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' },
        grid: { color: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' },
        pointLabels: {
          font: { size: windowWidth < 600 ? 10 : 12 },
        },
        ticks: { display: false },
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: windowWidth < 600 ? 10 : 12 },
        },
      },
      tooltip: { enabled: true },
    },
    maintainAspectRatio: false,
  };

  useEffect(() => {
    const tableElement = document.getElementById('performance-table');
    if (tableElement) setTableHeight(`${tableElement.offsetHeight}px`);
  }, [report, chartType]);

  if (loading) return <div>Loading...</div>;

  const tooltipContent = showOverallPerformance
    ? `The Monthly Performance Summary uses AI-enhanced insights to track your trading progress across all accounts, comparing monthly performance, analyzing win rates, confidence levels, check list adherence (if applicable), trade replays (if applicable), and providing personalized feedback with session trends to guide smarter trading decisions.`
    : `The Monthly Performance Summary uses AI-enhanced insights to track your trading progress for this account, comparing monthly performance, analyzing win rates, confidence levels, check list adherence (if applicable), trade replays (if applicable), and providing personalized feedback with session trends to guide smarter trading decisions.`;

  const metrics = [
    { label: "Total Trades", current: report.totalTrades, previous: report.twoMonthsAgoReport?.totalTrades, format: v => v !== undefined && v !== null ? v : '0' },
    { label: "Winning Trades", current: report.winningTrades, previous: report.twoMonthsAgoReport?.winningTrades, format: v => v !== undefined && v !== null ? v : '0' },
    { label: "Missed Trades (Positive)", current: (report.missedTrades || []).filter(trade => trade.returns > 0).length, previous: (report.twoMonthsAgoReport?.missedTrades || []).filter(trade => trade.returns > 0).length, format: v => v !== undefined && v !== null ? v : '0' },
    { label: "Win Rate", current: report.winRate, previous: report.twoMonthsAgoReport?.winRate, format: v => v !== undefined && v !== null ? `${v.toFixed(1)}%` : '0%' },
    { label: "Total Profit", current: report.totalProfit, previous: report.twoMonthsAgoReport?.totalProfit, format: v => v !== undefined && v !== null ? `$${v.toFixed(2)}` : '$0.00' },
    { label: "Profit Factor", current: report.profitFactor, previous: report.twoMonthsAgoReport?.profitFactor, format: v => v !== undefined && v !== null ? v.toFixed(2) : '0.00' },
    { label: "Avg Profit Per Trade", current: report.averageProfit, previous: report.twoMonthsAgoReport?.averageProfit, format: v => v !== undefined && v !== null ? `$${v.toFixed(2)}` : '$0.00' },
    { label: "Monthly Overall RR", current: report.riskRewardRatio, previous: report.twoMonthsAgoReport?.riskRewardRatio, format: v => v !== undefined && v !== null ? `1:${v.toFixed(2)}` : '1:0.00' },
    { label: "High Confidence Win Rate", current: report.highConfidenceWinRate, previous: report.twoMonthsAgoReport?.highConfidenceWinRate, format: v => v !== undefined && v !== null ? `${v.toFixed(2)}%` : '0%' },
    ...(report.hasFxbScore ? [{
      label: "Check List Adherence",
      current: report.planAdherence,
      previous: report.twoMonthsAgoReport?.planAdherence,
      format: v => v !== undefined && v !== null ? `${v.toFixed(2)}%` : '0%'
    }] : []),
    {
      label: "Total Trade Replays",
      current: report.totalReplays,
      previous: report.twoMonthsAgoReport?.totalReplays,
      format: v => v !== undefined && v !== null ? v : '0'
    },
    ...(report.totalReplays || report.twoMonthsAgoReport?.totalReplays ? [{
      label: "Average Replay Score",
      current: report.replayMetrics?.averageTotalScore,
      previous: report.twoMonthsAgoReport?.replayMetrics?.averageTotalScore,
      format: v => v !== undefined && v !== null ? `${parseFloat(v).toFixed(2)}/10` : '0.00/10'
    }] : []),
  ];

  return (
    <Card style={{ minHeight: '850px', height: '850px', position: 'relative' }}>
      <MDBox
        variant="gradient"
        bgColor="light"
        color="dark"
        coloredShadow="light"
        borderRadius="xl"
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="4rem"
        height="4rem"
        mt={-2}
        sx={{ position: 'absolute', left: '1rem', zIndex: 1 }}
      >
        <DateRangeIcon fontSize="medium" color="inherit" />
      </MDBox>
      <MDBox
        display="flex"
        flexDirection="column"
        pt={1}
        px={1}
        height="100%"
        sx={{
          overflowY: "auto",
          maxHeight: "850px",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
            borderRadius: "4px",
            "&:hover": {
              background: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: darkMode
            ? "rgba(255, 162, 235, 0.3) rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)",
        }}
      >
        <MDBox ref={nonTableContentRef}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDBox width="4rem" height="4rem" />
            <MDBox textAlign="right" lineHeight={1.25}>
              <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                Monthly Performance Summary: {`${getPreviousMonthName()} Report (${new Date().getFullYear()})`}
                {showOverallPerformance ? ' (All Trade Accounts)' 
                  : ` (${tradeAccounts.find(account => account.id === tradeAccountID)?.name || 'Unnamed Account'})`}
              </MDTypography>
              <Tooltip
                title={tooltipContent}
                placement="bottom-end"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1.5,
                      maxWidth: '250px',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      '& .MuiTooltip-arrow': {
                        color: theme.palette.background.paper,
                      }
                    }
                  }
                }}
              >
                <InfoIcon
                  sx={{
                    ml: 1,
                    fontSize: '1rem',
                    color: iconColor,
                    cursor: 'pointer'
                  }}
                />
              </Tooltip>
            </MDBox>
          </MDBox>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" p={1}>
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="button" color="text">Radar Graph</MDTypography>
              <Switch
                checked={chartType === 'table'}
                onChange={() => {
                  const newChartType = chartType === 'radar' ? 'table' : 'radar';
                  setChartType(newChartType);
                  saveChartPreference(newChartType);
                }}
              />
              <MDTypography variant="button" color="text">Table View</MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="button" color="text">All Trade Accounts (Overall)</MDTypography>
              <Switch
                checked={!(showOverallPerformance)}
                onChange={() => {
                  setShowOverallPerformance(!showOverallPerformance);
                  onTogglePerformanceView(!showOverallPerformance);
                }}
                disabled={!tradeAccountID}
              />
              <MDTypography variant="button" color="text">Selected Account</MDTypography>
            </MDBox>
          </MDBox>
          <MDBox p={1} mt={-4}>
            <Divider />
            <MDButton
              variant="gradient" 
              color="warning"
              onClick={handleOpenFeedbackModal}
              sx={{ mb: 1 }}
            >
              View {`${getPreviousMonthName()} Report (${new Date().getFullYear()})`} Feedback 
            </MDButton>
            <MDTypography
              variant="body2"
              color="text"
              fontWeight="bold"
            >
              FX Bullsheet Score: {(typeof report.score === 'string' ? parseFloat(report.score) : report.score || 0).toFixed(1)} / 10
            </MDTypography>
            <Divider sx={{ my: '1px' }} />
          </MDBox>
        </MDBox>
        <MDBox sx={{ overflowX: 'auto', flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {chartType === 'radar' ? (
            <MDBox sx={{ width: '100%', maxWidth: '600px', height: '400px', p: 2 }}>
              <Radar data={radarData} options={radarOptions} />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: `calc(850px - ${nonTableContentRef.current?.offsetHeight || 0}px - 10px)`,
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
              }}
            >
              <table
                id="performance-table"
                className="performance-summary-table"
                ref={tableRef}
                style={{
                  width: '100%',
                  tableLayout: 'fixed',
                  fontSize: tableFontSize,
                  minWidth: windowWidth < 600 ? '100%' : '100%'
                }}
              >
                <thead style={{ position: 'sticky', top: 0, background: theme.palette.background.default, zIndex: 1 }}>
                  <tr style={{ height: tableRowHeight }}>
                    <th style={{ whiteSpace: 'nowrap' }}>
                      <MDTypography sx={{ fontSize: 'inherit', color: 'text' }}>Metric</MDTypography>
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>
                      <MDTypography sx={{ fontSize: 'inherit', color: 'text' }}>Last Month</MDTypography>
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>
                      <MDTypography sx={{ fontSize: 'inherit', color: 'text' }}>Previous Month</MDTypography>
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>
                      <MDTypography sx={{ fontSize: 'inherit', color: 'text' }}>Trend</MDTypography>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((metric) => (
                    <tr key={metric.label} style={{ height: tableRowHeight }}>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <MDTypography variant="body2" fontWeight="regular" sx={{ fontSize: 'inherit' }}>
                          {metric.label}
                        </MDTypography>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: 'inherit' }}>
                          {metric.format(metric.current)}
                        </MDTypography>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <MDTypography variant="body2" color="text" sx={{ fontSize: 'inherit' }}>
                          {metric.previous !== undefined && metric.previous !== null ? metric.format(metric.previous) : '-'}
                        </MDTypography>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {metric.previous !== undefined && metric.previous !== null && metric.current !== undefined && metric.current !== null && (
                          <span className={`trend-indicator ${metric.current > metric.previous ? 'trend-up' : metric.current < metric.previous ? 'trend-down' : 'trend-neutral'}`}>
                            {metric.current > metric.previous ? '↑' : metric.current < metric.previous ? '↓' : '−'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <Modal
        open={openFeedbackModal}
        onClose={handleCloseFeedbackModal}
        aria-labelledby="feedback-modal-title"
        aria-describedby="feedback-modal-description"
      >
        <MDBox
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '60%' },
            maxWidth: '600px',
            bgcolor: theme.palette.background.default, // CHANGED: Use default for better theme consistency
            color: theme.palette.text.primary, // Ensure text respects theme
            borderRadius: '12px',
            boxShadow: 24,
            p: 4,
            maxHeight: '80vh',
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            '& *': { // Ensure all child elements respect theme colors
              color: theme.palette.text.primary,
            },
          }}
        >
          <MDTypography
            id="feedback-modal-title"
            variant="h6"
            component="h2"
            fontWeight="medium"
            mb={2}
          >
            Trading Feedback
          </MDTypography>
          <MDTypography
            id="feedback-modal-description"
            variant="body2"
            fontWeight="regular"
            sx={{ fontSize: feedbackFontSize, lineHeight: 1.2 }}
            ref={feedbackRef}
          >
            {report.feedback || 'No feedback available.'}
          </MDTypography>
          <MDBox mt={3} display="flex" justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color={darkMode ? "light" : "dark"}
              onClick={handleCloseFeedbackModal}
            >
              Close
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>
    </Card>
  );
};

MonthlyPerformanceSummary.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
  onTogglePerformanceView: PropTypes.func.isRequired,
  tradeAccounts: PropTypes.array.isRequired,
};

export default MonthlyPerformanceSummary;